/**
 * Куда встать прокрутке, чтобы полоса «сейчас» оказалась в верхней части
 * видимой области.
 *
 * Чистой функцией, а не тремя строками внутри хука, по той же причине, что у
 * `drag-edge.ts`: тестов на компоненты в проекте нет (раздел 9 CLAUDE.md), и
 * арифметика, оставшаяся внутри эффекта, не проверялась бы ничем вовсе.
 * Промахивается она молча и в обе стороны: недолёт показывает уже прошедший
 * день, перелёт уводит идущее занятие за верхнюю кромку окна.
 *
 * ⚠️ **Координаты тут двух видов, и путать их нельзя.** `nowTop` отсчитан от
 * ВИДИМОЙ КРОМКИ, то есть уже учитывает текущую прокрутку, и у полосы выше
 * кромки он отрицательный. Остальные три числа и результат отсчитаны от начала
 * ПРОКРУЧИВАЕМОГО СОДЕРЖИМОГО. Отсюда и вид формулы: видимую координату
 * складываем с прокруткой, и только потом зажимаем.
 *
 * ⚠️ Чем именно прокручивают, функция не знает и знать не должна. До
 * 17.09.2026 её кормили величинами окна (`window.scrollY`, `innerHeight`), с
 * того дня величинами тела календаря (`scrollTop`, `clientHeight`), и
 * арифметика не поменялась ни на знак. Имён окна поэтому здесь нет.
 *
 * Разбор в `docs/schedule-calendar-design.md`, раздел 9.
 */

/**
 * Сколько прошедшего дня остаётся видно НАД полосой: четверть высоты окна.
 *
 * Доля экрана, а не фиксированные пиксели и не «ровно один час». Пиксельный
 * запас на телефоне съел бы заметный кусок экрана, а на широком мониторе не
 * отличался бы от нуля; час запаса зависит от высоты часа (`--cal-hour`) и
 * уехал бы вместе с ней при первой же правке масштаба. Доля держит обещание
 * одинаково везде: сверху видно, чем день только что кончился (на телефоне это
 * около двух часов прошлого, на большом экране больше), а остальные три четверти
 * заняты будущим, ради которого человек в расписание и смотрит.
 */
export const HEADROOM_SHARE = 0.25;

/**
 * Мельче этого прокрутка не считается прокруткой.
 *
 * Порог закрывает два случая разом, и второй важнее. Экран уже стоит там, где
 * надо (второй заход на ту же неделю, повторное нажатие «Сегодня»), и календарь
 * целиком помещается в окно: там `maxScroll` около нуля, цель зажимается в ноль
 * и совпадает с текущей прокруткой. Без порога мы звали бы `scrollTo` ради
 * нулевого сдвига, то есть трогали бы окно там, где двигать нечего.
 */
export const TOLERANCE_PX = 2;

export function nowScrollTarget(view: {
  /** Текущая прокрутка: `scrollTop` у коробки либо `scrollY` у окна. */
  scrollY: number;
  /** Верх полосы «сейчас» относительно ВИДИМОЙ кромки, из `getBoundingClientRect`. */
  nowTop: number;
  /** Высота видимой области: `clientHeight` у коробки либо `innerHeight` у окна. */
  viewport: number;
  /**
   * Докуда прокрутка вообще доезжает: `scrollHeight` минус высота видимой
   * области. У содержимого короче неё приезжает ОТРИЦАТЕЛЬНЫМ, и это штатный
   * случай.
   */
  maxScroll: number;
}): number | null {
  const wanted = view.scrollY + view.nowTop - view.viewport * HEADROOM_SHARE;
  // Потолок приходится зажимать своими руками: у содержимого короче видимой
  // области `scrollHeight - clientHeight` приезжает отрицательным, и потолок ниже пола
  // увёл бы цель выше нуля, то есть в координату, которой не существует
  const target = Math.min(Math.max(wanted, 0), Math.max(view.maxScroll, 0));
  return Math.abs(target - view.scrollY) < TOLERANCE_PX ? null : target;
}
